'use client';

import React, { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { initializeGA, disableGA, reEnableGA, trackEvent } from '../../lib/analytics'

export default function Cookies() {
  const pathname = usePathname()
  const [showBanner, setShowBanner] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [acceptHovered, setAcceptHovered] = useState(false)
  const [rejectHovered, setRejectHovered] = useState(false)

  // Check for existing cookie on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('cookieConsent='))
        ?.split('=')[1]

      setShowBanner(!cookieValue)
      setIsLoaded(true)

      // Only turn tracking on if the user has explicitly accepted.
      // No cookie yet, rejected, or dismissed without choosing all mean: stay off.
      if (cookieValue === 'accepted') {
        initializeGA()
      } else if (cookieValue === 'rejected') {
        disableGA()
      }
    }
  }, [])

  // Trigger animation after 2 second delay
  useEffect(() => {
    if (showBanner && isLoaded) {
      const timer = setTimeout(() => {
        setIsAnimating(true)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showBanner, isLoaded])

  // Set cookie with 30 days expiration
  const setCookie = (value: string) => {
    if (typeof window !== 'undefined') {
      const expires = new Date()
      expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000)) // 30 days
      document.cookie = `cookieConsent=${value}; expires=${expires.toUTCString()}; path=/`
    }
  }

  const handleClose = () => {
    setCookie('dismissed')
    setShowBanner(false)
    // Dismissing without choosing is not consent - leave tracking off
  }

  const handleAccept = () => {
    setCookie('accepted')
    setShowBanner(false)
    // Re-enable GA in case user previously rejected (or ensure it's enabled)
    reEnableGA()
  }

  const handleReject = () => {
    // Track analytics for reject all cookies
    trackEvent('cookie_click', {
      click_text: 'Reject all cookies',
      page_path: pathname
    })
    
    setCookie('rejected')
    setShowBanner(false)
    // Disable GA tracking when user rejects
    disableGA()
  }

  // Don't render until we've checked for cookies to prevent flash
  if (!isLoaded || !showBanner) {
    return null
  }

  return (
    <div 
      className={`fixed bottom-4 left-4 right-4 md:right-auto bg-eggshell border border-black shadow-lg rounded-lg p-6 z-50 md:w-[40%] md:min-w-[450px] transition-all duration-300 ease-out ${
        isAnimating 
          ? 'opacity-100 scale-100' 
          : 'opacity-0 scale-0'
      }`}
      style={{ transformOrigin: 'bottom center' }}
    >
      <div className="flex justify-between items-start mb-3">
        <h2 className="text-xl font-bold text-gray-900">We use cookies</h2>
        <button 
          onClick={handleClose}
          aria-label="Close cookie notice"
          className="text-gray-500 hover:text-gray-700 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none rounded"
        >
          ×
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
        The Havas CX website uses technical cookies, as well as cookies to analyze traffic on the website. By clicking on &quot;Ok, accept all&quot;, you agree to cookies being stored on your device. If you click on &quot;Reject all cookies&quot; (except for technical cookies), the above will not take place or will be less specific. For more information, please read our{' '}
        <a 
          href="https://www.havas.com/cookie-policy/" 
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-600 hover:text-gray-900 underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
        >
          Cookie Policy
        </a>
        {' '}and our{' '}
        <a 
          href="https://www.havas.com/data-protection-policy/" 
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-600 hover:text-gray-900 underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
        >
          Data Privacy Policy
        </a>.
      </p>
      <div className="flex gap-2 mb-4">
        <button 
          onClick={handleAccept}
          onMouseEnter={() => setAcceptHovered(true)}
          onMouseLeave={() => setAcceptHovered(false)}
          className="inline-flex items-center border border-[#1B1B1B] px-2 md:px-4 py-2 rounded-full label-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-300 ease-out hover:bg-[#E60000] hover:border-[#E60000]"
        >
          <div className="cta-primary-button relative overflow-hidden px-2" style={{ willChange: 'transform' }}>
            <span 
              className={`block transition-transform duration-300 ease-out text-[#1B1B1B] ${
                acceptHovered ? '-translate-y-full' : 'translate-y-0'
              }`}
              style={{ willChange: 'transform' }}
            >
              Accept all cookies
            </span>
            <span 
              className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out text-eggshell ${
                acceptHovered ? 'translate-y-0' : 'translate-y-full'
              }`}
              style={{ willChange: 'transform' }}
            >
              Accept all cookies
            </span>
          </div>
        </button>
        <button 
          onClick={handleReject}
          onMouseEnter={() => setRejectHovered(true)}
          onMouseLeave={() => setRejectHovered(false)}
          className="inline-flex items-center border border-[#1B1B1B] px-2 md:px-4 py-2 rounded-full label-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-300 ease-out hover:bg-[#E60000] hover:border-[#E60000]"
        >
          <div className="cta-primary-button relative overflow-hidden px-2" style={{ willChange: 'transform' }}>
            <span 
              className={`block transition-transform duration-300 ease-out text-[#1B1B1B] ${
                rejectHovered ? '-translate-y-full' : 'translate-y-0'
              }`}
              style={{ willChange: 'transform' }}
            >
              Reject all cookies
            </span>
            <span 
              className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out text-eggshell ${
                rejectHovered ? 'translate-y-0' : 'translate-y-full'
              }`}
              style={{ willChange: 'transform' }}
            >
              Reject all cookies
            </span>
          </div>
        </button>
      </div>
    </div>
  )
}
